export type FieldValueType = 'boolean' | 'currency' | 'number' | 'string'

export interface FieldConfig {
  label: string
  valueType: FieldValueType
  // Staging/live column key to look up ideon/real data in rowData
  stagingKey?: string
  // Decimal precision for display and input formatting
  precision?: number
}

const AREA_FACTOR_FIELD_ENTRIES = Array.from({ length: 67 }, (_, i) => {
  const n = (i + 1).toString().padStart(2, '0')
  return [
    `ra${n}`,
    {
      label: `Area Factor RA${n}`,
      valueType: 'number' as FieldValueType,
      stagingKey: `areaFactor${n}`,
      precision: 8,
    },
  ]
})

export const FIELD_CONFIG: Record<string, FieldConfig> = {
  carrier: { label: 'Carrier', valueType: 'string', stagingKey: 'carrier' },
  planName: {
    label: 'Plan Name',
    valueType: 'string',
    stagingKey: 'planMarketingName',
  },
  householdName: {
    label: 'Household Name',
    valueType: 'string',
    stagingKey: 'carrierFriendlyName',
  },
  planType: {
    label: 'Plan Type',
    valueType: 'string',
    stagingKey: 'planType',
  },
  level: { label: 'Metal Level', valueType: 'string', stagingKey: 'metal' },
  hsa: { label: 'HSA', valueType: 'boolean', stagingKey: 'isHsa' },
  networkName: {
    label: 'Network Name',
    valueType: 'string',
    stagingKey: 'providerNetwork',
  },
  isForSale: {
    label: 'For Sale',
    valueType: 'boolean',
    stagingKey: 'isForSale',
  },
  proofOfResidenceRequired: {
    label: 'Proof of Residence Required',
    valueType: 'boolean',
    stagingKey: 'proofOfResidenceRequired',
  },
  serviceAreaId: {
    label: 'Service Area ID',
    valueType: 'string',
    stagingKey: 'serviceAreaId',
  },
  baseRate: {
    label: 'Base Rate',
    valueType: 'currency',
    stagingKey: 'baseRate',
    precision: 8,
  },
  planFee: {
    label: 'Plan Fee',
    valueType: 'currency',
    stagingKey: 'planFee',
  },
  planStatus: {
    label: 'Plan Status',
    valueType: 'string',
    stagingKey: 'planStatus',
  },
  crosswalkPlanId: {
    label: 'Crosswalk Plan ID',
    valueType: 'string',
    stagingKey: 'crosswalkHiosId',
  },
  onExchange: {
    label: 'On Exchange',
    valueType: 'boolean',
    stagingKey: 'onExchange',
  },
  offExchange: {
    label: 'Off Exchange',
    valueType: 'boolean',
    stagingKey: 'offExchange',
  },
  actuarialValue: {
    label: 'Actuarial Value',
    valueType: 'number',
    stagingKey: 'actuarialValue',
    precision: 4,
  },
  tooltip: {
    label: 'Tooltip',
    valueType: 'string',
    stagingKey: 'tooltip',
  },
  ...Object.fromEntries(AREA_FACTOR_FIELD_ENTRIES),
}

export const IDENTITY_FIELDS = new Set(['year', 'hiosPlanId', 'state'])

// Grid column key → override param flat key (for popover)
export const GRID_COL_TO_PARAM_FIELD: Record<string, string> = {
  planMarketingName: 'planName',
  metal: 'level',
  carrierFriendlyName: 'householdName',
  isHsa: 'hsa',
  providerNetwork: 'networkName',
  crosswalkHiosId: 'crosswalkPlanId',
  ...Object.fromEntries(
    Array.from({ length: 67 }, (_, i) => {
      const n = (i + 1).toString().padStart(2, '0')
      return [`areaFactor${n}`, `ra${n}`]
    })
  ),
}

// Maps flat area factor key → rateAdjustments sub-key
export const RA_KEY_MAP: Record<string, string> = Object.fromEntries(
  Array.from({ length: 67 }, (_, i) => {
    const n = (i + 1).toString().padStart(2, '0')
    return [`ra${n}`, `RA${n}`]
  })
)

// Area factor param keys (ra01..ra67)
export const AREA_FACTOR_KEYS = new Set(
  Array.from(
    { length: 67 },
    (_, i) => `ra${(i + 1).toString().padStart(2, '0')}`
  )
)

// Grid columns that must not be editable (identity keys only — build natural_key_hash)
export const PLANS_AND_RATES_NON_EDITABLE_COLS = new Set([
  'year',
  'hiosPlanId',
  'state',
])

// Expand rule.params (with nested rateAdjustments) into flat FIELD_CONFIG keys
export function flattenRuleParams(
  params: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...params }
  delete result.rateAdjustments
  const ra = params.rateAdjustments as Record<string, unknown> | undefined
  if (ra) {
    for (const [raKey, value] of Object.entries(ra)) {
      // raKey is like 'RA01'
      const n = raKey.slice(2) // '01'
      result[`ra${n}`] = value
    }
  }
  return result
}

// Collapse flat FIELD_CONFIG keys back into nested params (with rateAdjustments)
export function nestRuleParams(
  flat: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  const rateAdjustments: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(flat)) {
    const raSubKey = RA_KEY_MAP[key]
    if (raSubKey) {
      rateAdjustments[raSubKey] = value
    } else {
      result[key] = value
    }
  }
  if (Object.keys(rateAdjustments).length > 0) {
    result.rateAdjustments = rateAdjustments
  }
  return result
}

export function coerceParamValue(
  field: string,
  value: unknown
): string | number | boolean | null {
  if (value === null || value === undefined) return null
  const valueType = FIELD_CONFIG[field]?.valueType
  if (valueType === 'boolean') return Boolean(value)
  if (valueType === 'number' || valueType === 'currency') {
    const n = Number(value)
    return isNaN(n) ? null : n
  }
  return String(value)
}

// Get the ideon/staging value for a FIELD_CONFIG field from rowData
export function getStagingValue(
  field: string,
  rowData: Record<string, unknown>
): unknown {
  const stagingKey = FIELD_CONFIG[field]?.stagingKey
  if (!stagingKey) return rowData[field] ?? null
  // For area factors, check __overrideParams.rateAdjustments or direct staging key
  return rowData[stagingKey] ?? null
}

// Get the override value for a FIELD_CONFIG field from overrideParams.
// Area factor fields (ra01–ra67) may be stored flat (upsert-field path) or
// nested under rateAdjustments (full modal save path) — check both.
export function getOverrideValue(
  field: string,
  overrideParams: Record<string, unknown> | null
): unknown {
  if (!overrideParams) return undefined
  const raSubKey = RA_KEY_MAP[field]
  if (raSubKey) {
    if (field in overrideParams) return overrideParams[field]
    const ra = overrideParams.rateAdjustments as
      | Record<string, unknown>
      | undefined
    if (ra && raSubKey in ra) return ra[raSubKey]
    return undefined
  }
  if (field in overrideParams) return overrideParams[field]
  return undefined
}
